import { useState } from "react";
import { useRule } from "../CreateRuleNew/RuleReducer";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import Button3 from "../../../Button/Button3";
import classes from "./RuleWizard.module.css";
import { InputNumber } from "primereact/inputnumber";
import { Dropdown } from "primereact/dropdown";

const WizardStepRestrictions = ({ onNext, onBack }) => {
    const { state, dispatch } = useRule();

    const isLeftOpen = state.inputType === 5;
    const hasAbsenceCondition = state.conditions.some((c) => c.is_absence_condition);
    // Deadline checks are instantaneous status checks, not conditions waiting to become true —
    // they don't need a timeframe, so they don't count toward requiring one.
    const hasMultipleConditions = state.conditions.filter((c) => !c.is_deadline_condition).length > 1;
    const requiresTimeframe = hasAbsenceCondition
    const [showTimeFrameOptions, setShowTimeFrameOptions] = useState(isLeftOpen || requiresTimeframe);
    const initialTimeframe = (() => {
        const ms = state.timeframe_window_ms;
        if (!ms) return { value: "", unit: 60000 };
        if (ms % 3600000 === 0) return { value: ms / 3600000, unit: 3600000 };
        if (ms % 60000 === 0) return { value: ms / 60000, unit: 60000 };
        return { value: ms / 1000, unit: 1000 };
    })();
    const [timeframeValue, setTimeframeValue] = useState(initialTimeframe.value);
    const [timeframeUnit, setTimeframeUnit] = useState(initialTimeframe.unit);
    const [timeframeDescriptionVisible, setTimeframeDescriptionVisible] = useState(false);

    const TIMEFRAME_UNITS = [
        { label: "Seconds", value: 1000 },
        { label: "Minutes", value: 60000 },
        { label: "Hours",   value: 3600000 },
    ];

    const canProceed = !requiresTimeframe || timeframeValue > 0;

    const renderRequirementBadge = (isRequired) => (
        <span style={{
            display: "inline-block", alignSelf: "flex-start", fontSize: "11px", fontWeight: 700,
            textTransform: "uppercase", letterSpacing: "0.4px", padding: "2px 8px", borderRadius: "10px",
            backgroundColor: isRequired ? "#fdecea" : "#eef1f5",
            color: isRequired ? "#c0392b" : "#6e6e6e",
        }}>
            {isRequired ? "Required" : "Optional"}
        </span>
    );

    return (
        <>
            <div className={classes.contentHeader}>
                <h2 className={classes.contentTitle}>Timeframe</h2>
                <p className={classes.contentSubtitle}>
                    {hasAbsenceCondition
                        ? "A timeframe is required because one of your conditions checks for something that hasn't happened."
                        : hasMultipleConditions
                            ? "A timeframe is required because this rule has more than one condition."
                            : "Optionally restrict the timeframe between conditions."}
                </p>
            </div>

            <div style={{ flex: 1, overflowY: "auto", display: "flex", flexDirection: "column", gap: "14px" }}>
                {(hasMultipleConditions || hasAbsenceCondition) && (
                <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                    {renderRequirementBadge(requiresTimeframe)}
                    {!showTimeFrameOptions ? (
                    <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                        <Button
                            label="Set timeframe between conditions"
                            icon="pi pi-clock"
                            size="small"
                            severity="info"
                            rounded
                            outlined
                            style={{ alignSelf: "flex-start" }}
                            onClick={() => setShowTimeFrameOptions(true)}
                        />
                        <Button
                            icon="pi pi-question-circle"
                            rounded
                            text
                            size="small"
                            severity="secondary"
                            aria-label="What does this do?"
                            onClick={() => setTimeframeDescriptionVisible(true)}
                        />
                    </div>
                    ) : (
                    <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
                            <span style={{ fontSize: "12px", color: "#6e6e6e" }}>Within:</span>
                            <InputNumber
                                value={timeframeValue === "" ? null : Number(timeframeValue)}
                                onValueChange={(e) => {
                                    const val = e.value ?? "";
                                    setTimeframeValue(val);
                                    dispatch({
                                        type: "SET_TIMEFRAME_WINDOW_MS",
                                        payload: val > 0 ? Number(val) * timeframeUnit : null,
                                    });
                                }}
                                min={1}
                                style={{ width: "80px", flexShrink: 0 }}
                                inputStyle={{ width: "80px" }}
                                placeholder=""
                            />
                            <Dropdown
                                value={timeframeUnit}
                                options={TIMEFRAME_UNITS}
                                onChange={(e) => {
                                    setTimeframeUnit(e.value);
                                    if (timeframeValue > 0) {
                                        dispatch({ type: "SET_TIMEFRAME_WINDOW_MS", payload: Number(timeframeValue) * e.value });
                                    }
                                }}
                                style={{ width: "180px", flexShrink: 0 }}
                            />
                            {!requiresTimeframe && (
                                <Button
                                    icon="pi pi-times"
                                    size="small"
                                    severity="secondary"
                                    text
                                    onClick={() => {
                                        setShowTimeFrameOptions(false);
                                        setTimeframeValue("");
                                        dispatch({ type: "SET_TIMEFRAME_WINDOW_MS", payload: null });
                                    }}
                                />
                            )}
                        </div>
                        <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
                            <span style={{ fontSize: "12px", color: "#6e6e6e" }}>Window:</span>
                            <Button3
                                style={{
                                    minWidth: "90px", minHeight: "30px", fontSize: "12px",
                                    backgroundColor: state.timeframe_window_mode === "FIXED" ? "var(--Primary-Color)" : undefined,
                                    color: state.timeframe_window_mode === "FIXED" ? "white" : undefined,
                                }}
                                onClick={() => dispatch({ type: "SET_TIMEFRAME_WINDOW_MODE", payload: "FIXED" })}
                            >
                                Fixed
                            </Button3>
                            <Button3
                                style={{
                                    minWidth: "90px", minHeight: "30px", fontSize: "12px",
                                    backgroundColor: state.timeframe_window_mode === "SLIDING" ? "var(--Primary-Color)" : undefined,
                                    color: state.timeframe_window_mode === "SLIDING" ? "white" : undefined,
                                }}
                                onClick={() => dispatch({ type: "SET_TIMEFRAME_WINDOW_MODE", payload: "SLIDING" })}
                            >
                                Sliding
                            </Button3>
                        </div>
                        <p style={{ margin: 0, fontSize: "12px", color: "#6e6e6e" }}>
                            {state.timeframe_window_mode === "SLIDING"
                                ? "Sliding: the countdown restarts every time any new trigger event occurs, as long as it happens within the window. For example, if you have 2 triggers for this rule, and only 1 of them changes, the countdown will restart."
                                : "Fixed: the countdown starts once and keeps running, even if another trigger event occurs within the window."}
                        </p>
                        {isLeftOpen && (
                            <p style={{ margin: 0, fontSize: "12px", color: "#6e6e6e" }}>
                                This sets the length of time the lock must be left open before the rule fires.
                            </p>
                        )}
                        {!isLeftOpen && hasAbsenceCondition && (
                            <p style={{ margin: 0, fontSize: "12px", color: "#6e6e6e" }}>
                                One of your conditions checks for something that hasn't happened, so a timeframe
                                is required to define the window it's checked within.
                            </p>
                        )}
                        {!isLeftOpen && !hasAbsenceCondition && hasMultipleConditions && (
                            <p style={{ margin: 0, fontSize: "12px", color: "#6e6e6e" }}>
                                This rule has more than one condition, so a timeframe is required to define the
                                window they must all become true within.
                            </p>
                        )}
                    </div>
                    )}
                </div>
                )}
            </div>

            <div className={classes.navRow}>
                <Button label="Back" icon="pi pi-arrow-left" severity="secondary" outlined onClick={onBack} />
                <Button
                    label="Next"
                    icon="pi pi-arrow-right"
                    iconPos="right"
                    severity="success"
                    disabled={!canProceed}
                    onClick={onNext}
                />
            </div>

            <Dialog
                header="What does timeframe between conditions do?"
                visible={timeframeDescriptionVisible}
                style={{ width: "34vw" }}
                onHide={() => setTimeframeDescriptionVisible(false)}
            >
                <p style={{ margin: 0, fontSize: "14px", color: "#3d3d3d", lineHeight: 1.6 }}>
                    This sets the length of time all of the selected conditions must be satisfied in for the
                    rule to execute — every condition needs to become true within that window, not just at
                    some point in general.
                </p>
                <p style={{ margin: "12px 0 0 0", fontSize: "14px", color: "#3d3d3d", lineHeight: 1.6 }}>
                    For example: a 30 second timeframe means Condition 1 and Condition 2 must both be true
                    within 30 seconds of each other for the rule to fire.
                </p>
                <p style={{ margin: "12px 0 0 0", fontSize: "14px", color: "#3d3d3d", lineHeight: 1.6 }}>
                    <strong>Fixed</strong> vs <strong>Sliding</strong> controls what happens to the countdown when another
                    trigger event occurs while it's still running. With Fixed, the countdown keeps going regardless.
                    With Sliding, each new trigger event resets the countdown back to the full window.
                </p>
                <br />
                <div style={{ display: "flex", justifyContent: "flex-end" }}>
                    <Button label="Got it" severity="success" onClick={() => setTimeframeDescriptionVisible(false)} />
                </div>
            </Dialog>
        </>
    );
};

export default WizardStepRestrictions;
